import { Box } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CheckboxGroupField from 'components/forms/fields/checkbox-group-field/checkbox-group-field.component';
import { useLocationHoursContext } from 'components/location-hours/location-hours.context';
import DateRangeField from 'components/forms/fields/date-range-field/date-range-field.component';
import { filterTemporaryHoursOverlappedEvents } from 'components/location-hours/temporary-hours-calendar/temporary-hours.utils';
import { DateTime } from 'luxon';
import TimeRangePickerField from 'components/forms/fields/time-range-picker-field/time-range-picker-field.component';

type SetTemporaryHoursFormProps = {
  eventId: string;
};
const SetTemporaryHoursForm = ({ eventId }: SetTemporaryHoursFormProps) => {
  const { t } = useTranslation();
  const orderType = useWatch({ name: 'orderType' });
  const dateRange = useWatch({ name: 'dateRange' });
  const timeRange = useWatch({ name: 'timeRange' });
  const pickupClosed = useWatch({ name: 'pickupClosed' });
  const deliveryClosed = useWatch({ name: 'deliveryClosed' });
  const {
    temporaryHours: { temporaryEvents },
  } = useLocationHoursContext();

  const orderTypeOptions = [
    {
      label: t('forms.setTemporaryHoursForm.fields.orderType.options.pickup'),
      id: 'PICKUP',
      disabled: pickupClosed,
    },
    {
      label: t('forms.setTemporaryHoursForm.fields.orderType.options.delivery'),
      id: 'DELIVERY',
      disabled: deliveryClosed,
    },
  ];

  const hasOverlap =
    filterTemporaryHoursOverlappedEvents(
      { eventId, orderType, dateRange, timeRange },
      temporaryEvents,
    ).length > 0;

  return (
    <Box className="field-container" display="grid" gap={3}>
      <CheckboxGroupField
        name="orderType"
        options={orderTypeOptions}
        FormGroupProps={{
          sx: {
            flexDirection: 'row',
          },
        }}
      />

      <DateRangeField
        name="dateRange"
        label={t('forms.setTemporaryHoursForm.fields.dateRange.label')}
        data-testid="SetTemporaryHoursForm.DateRange.Field"
        sx={{ mt: 0 }}
        // Can't use disablePast due to testing purposes
        shouldDisableDate={date => date < DateTime.now().startOf('day')}
      />

      <TimeRangePickerField
        name="timeRange"
        helperText={
          hasOverlap &&
          t('forms.setTemporaryHoursForm.fields.timeRange.warnings.overlap')
        }
        helperTextSeverity="warning"
        minutesStep={15}
        fromLabel={t('forms.setTemporaryHoursForm.fields.timeRange.fromLabel')}
        toLabel={t('forms.setTemporaryHoursForm.fields.timeRange.toLabel')}
        data-testid="SetTemporaryHoursForm.TimeRange.Field"
        disabled={pickupClosed && deliveryClosed}
      />
    </Box>
  );
};

export default SetTemporaryHoursForm;
